import type {
  ConfigByObject,
  DraftFieldMapping,
  RelationshipMapping,
  RoleMapping,
  WritebackConfig,
  WritebackFieldMapping,
} from "../../../hubspot/new-flow/types";
import type {
  FieldDefinition,
  HubSpotField,
} from "../../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";
import { toBackendSlug } from "../adapters";

type DbFieldMappingPayload = {
  source_field_name: string;
  source_field_id: string;
  destination_field_id: string | null;
  destination_field_type: "field" | "metric" | "relation";
  destination_metric_name?: string;
  metric_aggregation_type?: "value" | "delta";
  is_owner_assignment?: boolean;
  is_relationship_assignment?: boolean;
  is_required?: boolean;
  transform_rules?: Record<string, any>;
};

export type CommitStage =
  | "ensure_inbound_sync"
  | "update_inbound_config"
  | "save_inbound_field_mappings"
  | "save_integration_import_value"
  | "ensure_writeback_sync"
  | "update_writeback_config"
  | "save_writeback_field_mappings"
  | "save_integration_export_value"
  | "disable_writeback";

export type SaveRunResult = {
  object: string;
  stage: CommitStage;
  ok: boolean;
  error?: string;
  retriable?: boolean;
};

interface CommitObjectDependencies {
  ensureInboundSync: (slug: string, sourceCollection?: string) => Promise<string | undefined>;
  ensureWritebackSync: (slug: string, inboundSyncId: string, sourceCollection?: string) => Promise<string | undefined>;
  updateSyncConfiguration: (syncId: string, data: Record<string, any>) => Promise<void>;
  getFieldMappings: (syncId: string) => Promise<any[]>;
  updateFieldMapping: (syncId: string, mappingId: string, data: Record<string, any>) => Promise<void>;
  deleteFieldMapping: (syncId: string, mappingId: string) => Promise<void>;
  createBulkFieldMappings: (syncId: string, mappings: Record<string, any>[]) => Promise<void>;
  patchIntegrationFieldMapping: (
    key: string,
    payload: { importValue?: Record<string, any>; exportValue?: Record<string, any> },
  ) => Promise<void>;
}

interface CommitObjectParams {
  object: "accounts" | "companies" | "contacts" | "deals";
  workspaceObjectId: string;
  providerKey: string;
  sourceCollection: string;
  config: ConfigByObject[string];
  draftMappings: DraftFieldMapping[];
  roleMappings: RoleMapping[];
  relationshipMappings: RelationshipMapping[];
  writebackConfig: WritebackConfig;
  writebackMappings: WritebackFieldMapping[];
  workspaceFields: FieldDefinition[];
  providerFields: HubSpotField[];
  systemMappingFields: Array<{ sourceFieldName: string; destinationFieldName: string }>;
  inboundSyncId?: string;
  writebackSyncId?: string;
  dependencies: CommitObjectDependencies;
}

interface CommitObjectOutput {
  inboundSyncId: string;
  writebackSyncId?: string;
  results: SaveRunResult[];
}

const normalize = (value?: string | null): string => String(value || "").trim();

const isListLikeProviderField = (field?: HubSpotField): boolean => {
  if (!field) return false;
  const type = normalize(field.type).toLowerCase();
  const key = normalize(field.key).toLowerCase();
  const title = normalize(field.title).toLowerCase();

  return (
    type.includes("array")
    || type.includes("list")
    || type === "enumeration"
    || key.includes("list")
    || key.includes("array")
    || title.includes("list")
    || title.includes("array")
  );
};

const findWorkspaceFieldByName = (
  workspaceFields: FieldDefinition[],
  fieldName?: string | null,
) => (
  workspaceFields.find((field) => field.name === fieldName || field.column_name === fieldName)
);

const resolveFieldType = (
  workspaceFields: FieldDefinition[],
  destinationFieldId?: string | null,
): "field" | "metric" => {
  if (!destinationFieldId) return "field";
  const destinationField = workspaceFields.find((field) => field.id === destinationFieldId);
  return destinationField?.attribute_type === "metric_traits" ? "metric" : "field";
};

const buildInboundDbMappings = ({
  draftMappings,
  matcherFieldName,
  matcherHyperengageFieldName,
  workspaceFields,
  systemMappingFields,
  roleMappings,
}: {
  draftMappings: DraftFieldMapping[];
  matcherFieldName?: string;
  matcherHyperengageFieldName?: string;
  workspaceFields: FieldDefinition[];
  systemMappingFields: Array<{ sourceFieldName: string; destinationFieldName: string }>;
  roleMappings: RoleMapping[];
}): DbFieldMappingPayload[] => {
  const output: DbFieldMappingPayload[] = [];

  draftMappings.forEach((mapping) => {
    const sourceFieldName = normalize(mapping.source_field_name);
    const destinationFieldId = normalize(mapping.destination_field_id);
    if (!sourceFieldName || !destinationFieldId || mapping.mapping_status !== "mapped") return;

    const destinationFieldType = resolveFieldType(workspaceFields, destinationFieldId);
    const destinationField = workspaceFields.find((field) => field.id === destinationFieldId);
    output.push({
      source_field_name: sourceFieldName,
      source_field_id: sourceFieldName,
      destination_field_type: destinationFieldType,
      destination_field_id: destinationFieldType === "metric" ? null : destinationFieldId,
      destination_metric_name: destinationFieldType === "metric" ? destinationField?.name : undefined,
      metric_aggregation_type: destinationFieldType === "metric"
        ? (mapping.metric_aggregation_type || "value")
        : undefined,
      is_owner_assignment: false,
      is_required: mapping.is_required || false,
      transform_rules: mapping.transform_rules || {},
    });
  });

  const matcherField = findWorkspaceFieldByName(workspaceFields, matcherHyperengageFieldName || "external_id");
  if (normalize(matcherFieldName) && matcherField?.id) {
    const matcherType = matcherField.attribute_type === "metric_traits" ? "metric" : "field";
    output.push({
      source_field_name: normalize(matcherFieldName),
      source_field_id: normalize(matcherFieldName),
      destination_field_type: matcherType,
      destination_field_id: matcherType === "metric" ? null : matcherField.id,
      destination_metric_name: matcherType === "metric" ? matcherField.name : undefined,
      metric_aggregation_type: matcherType === "metric" ? "value" : undefined,
      is_owner_assignment: false,
      is_required: true,
      transform_rules: {},
    });
  }

  systemMappingFields.forEach((systemField) => {
    const destinationField = findWorkspaceFieldByName(workspaceFields, systemField.destinationFieldName);
    if (!destinationField?.id) return;

    output.push({
      source_field_name: normalize(systemField.sourceFieldName),
      source_field_id: normalize(systemField.sourceFieldName),
      destination_field_type: "field",
      destination_field_id: destinationField.id,
      is_owner_assignment: false,
      is_required: true,
      transform_rules: {},
    });
  });

  roleMappings.forEach((roleMapping) => {
    const sourceFieldName = normalize(roleMapping.hubspot_owner_field);
    const roleField = findWorkspaceFieldByName(workspaceFields, roleMapping.hyperengage_role);
    if (!sourceFieldName || !roleField?.id) return;

    const roleFieldType = roleField.attribute_type === "metric_traits" ? "metric" : "field";
    output.push({
      source_field_name: sourceFieldName,
      source_field_id: sourceFieldName,
      destination_field_type: roleFieldType,
      destination_field_id: roleFieldType === "metric" ? null : roleField.id,
      destination_metric_name: roleFieldType === "metric" ? roleField.name : undefined,
      metric_aggregation_type: roleFieldType === "metric" ? "value" : undefined,
      is_owner_assignment: true,
      is_required: false,
      transform_rules: {},
    });
  });

  return output;
};

const buildWritebackDbMappings = ({
  writebackMappings,
  workspaceFields,
}: {
  writebackMappings: WritebackFieldMapping[];
  workspaceFields: FieldDefinition[];
}): DbFieldMappingPayload[] => {
  const output: DbFieldMappingPayload[] = [];

  writebackMappings.forEach((mapping) => {
    const sourceFieldName = normalize(mapping.hubspot_field);
    const destinationFieldId = normalize(mapping.hyperengage_field);
    if (!sourceFieldName || !destinationFieldId) return;

    const destinationField = workspaceFields.find((field) => field.id === destinationFieldId);
    const destinationType = destinationField?.attribute_type === "metric_traits" ? "metric" : "field";
    output.push({
      source_field_name: sourceFieldName,
      source_field_id: sourceFieldName,
      destination_field_type: destinationType,
      destination_field_id: destinationType === "metric" ? null : destinationFieldId,
      destination_metric_name: destinationType === "metric" ? destinationField?.column_name || destinationField?.name : undefined,
      metric_aggregation_type: destinationType === "metric" ? (mapping.metric_aggregation_type || "value") : undefined,
      is_owner_assignment: false,
      is_required: mapping.is_required || false,
      transform_rules: {},
    });
  });

  return output;
};

const mappingSignature = (mapping: DbFieldMappingPayload): string => {
  const destination = mapping.destination_field_type === "metric"
    ? `metric:${normalize(mapping.destination_metric_name)}`
    : `field:${normalize(mapping.destination_field_id)}`;

  return [
    normalize(mapping.source_field_name),
    destination,
    mapping.is_owner_assignment ? "owner" : "regular",
    mapping.is_relationship_assignment ? "relationship" : "not-relationship",
  ].join("|");
};

const persistDbMappings = async ({
  syncId,
  desiredMappings,
  dependencies,
}: {
  syncId: string;
  desiredMappings: DbFieldMappingPayload[];
  dependencies: CommitObjectDependencies;
}) => {
  const existingMappings = await dependencies.getFieldMappings(syncId);
  const existingBySignature = new Map<string, any>();
  existingMappings.forEach((mapping) => {
    const signature = mappingSignature({
      source_field_name: mapping.source_field_name,
      source_field_id: mapping.source_field_id || mapping.source_field_name,
      destination_field_id: mapping.destination_field_id,
      destination_field_type: mapping.destination_field_type || "field",
      destination_metric_name: mapping.destination_metric_name,
      is_owner_assignment: Boolean(mapping.is_owner_assignment),
      is_relationship_assignment: Boolean(mapping.is_relationship_assignment),
    });
    existingBySignature.set(signature, mapping);
  });

  const desiredSignatures = new Set<string>();
  const createPayload: DbFieldMappingPayload[] = [];
  const updatePayload: Array<{ mappingId: string; data: DbFieldMappingPayload }> = [];

  desiredMappings.forEach((mapping) => {
    const signature = mappingSignature(mapping);
    desiredSignatures.add(signature);

    const existing = existingBySignature.get(signature);
    if (existing?.id) {
      updatePayload.push({ mappingId: existing.id, data: mapping });
      return;
    }

    createPayload.push(mapping);
  });

  const deletePayload = existingMappings
    .filter((mapping) => !desiredSignatures.has(mappingSignature({
      source_field_name: mapping.source_field_name,
      source_field_id: mapping.source_field_id || mapping.source_field_name,
      destination_field_id: mapping.destination_field_id,
      destination_field_type: mapping.destination_field_type || "field",
      destination_metric_name: mapping.destination_metric_name,
      is_owner_assignment: Boolean(mapping.is_owner_assignment),
      is_relationship_assignment: Boolean(mapping.is_relationship_assignment),
    })))
    .map((mapping) => mapping.id)
    .filter(Boolean);

  const failedUpdatesForCreate: DbFieldMappingPayload[] = [];

  await Promise.all(updatePayload.map(async ({ mappingId, data }) => {
    try {
      await dependencies.updateFieldMapping(syncId, mappingId, data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        failedUpdatesForCreate.push(data);
        return;
      }
      throw error;
    }
  }));

  await Promise.all(deletePayload.map(async (mappingId) => {
    try {
      await dependencies.deleteFieldMapping(syncId, mappingId);
    } catch (error: any) {
      if (error?.response?.status === 404) return;
      throw error;
    }
  }));

  const allCreates = [...createPayload, ...failedUpdatesForCreate];
  if (allCreates.length > 0) {
    await dependencies.createBulkFieldMappings(syncId, allCreates);
  }
};

const buildImportValue = ({
  object,
  providerKey,
  draftMappings,
  matcherFieldName,
  matcherHyperengageFieldName,
  workspaceFields,
  systemMappingFields,
  roleMappings,
  relationshipMappings,
  providerFields,
}: {
  object: "accounts" | "companies" | "contacts" | "deals";
  providerKey: string;
  draftMappings: DraftFieldMapping[];
  matcherFieldName?: string;
  matcherHyperengageFieldName?: string;
  workspaceFields: FieldDefinition[];
  systemMappingFields: Array<{ sourceFieldName: string; destinationFieldName: string }>;
  roleMappings: RoleMapping[];
  relationshipMappings: RelationshipMapping[];
  providerFields: HubSpotField[];
}): Record<string, any> => {
  const importValue: Record<string, any> = {};

  draftMappings.forEach((mapping) => {
    const sourceFieldName = normalize(mapping.source_field_name);
    const destinationFieldId = normalize(mapping.destination_field_id);
    if (!sourceFieldName || !destinationFieldId || mapping.mapping_status !== "mapped") return;

    const destinationField = workspaceFields.find((field) => field.id === destinationFieldId);
    const destinationName = destinationField?.name || destinationField?.column_name;
    if (!destinationName) return;
    importValue[destinationName] = { $var: `$.${sourceFieldName}` };
  });

  const matcherFieldNameNormalized = normalize(matcherFieldName);
  const matcherHyperengageFieldNameNormalized = normalize(matcherHyperengageFieldName || "external_id");
  if (matcherFieldNameNormalized && matcherHyperengageFieldNameNormalized) {
    importValue[matcherHyperengageFieldNameNormalized] = {
      $var: `$.${matcherFieldNameNormalized}`,
    };
  }

  systemMappingFields.forEach((systemField) => {
    const sourceFieldName = normalize(systemField.sourceFieldName);
    const destinationFieldName = normalize(systemField.destinationFieldName);
    if (!sourceFieldName || !destinationFieldName) return;

    importValue[destinationFieldName] = { $var: `$.${sourceFieldName}` };
  });

  roleMappings.forEach((roleMapping) => {
    const roleKey = normalize(roleMapping.hyperengage_role);
    const ownerField = normalize(roleMapping.hubspot_owner_field);
    if (!roleKey || !ownerField) return;

    importValue[roleKey] = {
      $findAppRecordId: {
        dataLinkTable: {
          key: "users",
        },
        recordId: {
          $var: `$.${ownerField}`,
        },
      },
    };
  });

  if (object === "contacts") {
    const relationships: Record<string, any> = {};

    relationshipMappings.forEach((mapping) => {
      const targetObject = mapping.target_object;
      const listField = normalize(mapping.hubspot_list_field);
      if (!targetObject || !listField) return;

      const providerField = providerFields.find((field) => field.key === listField);
      const asList = isListLikeProviderField(providerField);
      const relationshipKey = `${targetObject}_${listField}`;
      const relationType = targetObject === "accounts" ? "users" : "user-company";
      const userRecordIdVar = providerKey.toLowerCase() === "salesforce"
        ? (asList ? "$.parent.Id" : "$.Id")
        : (asList ? "$.parent.hs_object_id" : "$.hs_object_id");

      if (asList) {
        relationships[relationshipKey] = {
          $iterate: {
            source: { $var: `$.${listField}` },
            item: {
              relation_type: relationType,
              account_id: {
                $findAppRecordId: {
                  dataLinkTable: { key: targetObject },
                  recordId: { $var: "$.item" },
                },
              },
              user_id: {
                $findAppRecordId: {
                  dataLinkTable: { key: "contacts" },
                  recordId: { $var: userRecordIdVar },
                },
              },
            },
          },
        };
      } else {
        relationships[relationshipKey] = [{
          relation_type: relationType,
          account_id: {
            $findAppRecordId: {
              dataLinkTable: { key: targetObject },
              recordId: { $var: `$.${listField}` },
            },
          },
          user_id: {
            $findAppRecordId: {
              dataLinkTable: { key: "contacts" },
              recordId: { $var: userRecordIdVar },
            },
          },
        }];
      }
    });

    if (Object.keys(relationships).length > 0) {
      importValue.relationships = relationships;
    }
  }

  return importValue;
};

const buildExportValue = ({
  writebackMappings,
  workspaceFields,
}: {
  writebackMappings: WritebackFieldMapping[];
  workspaceFields: FieldDefinition[];
}): Record<string, any> => {
  const exportValue: Record<string, any> = {};

  writebackMappings.forEach((mapping) => {
    const sourceField = normalize(mapping.hubspot_field);
    const destinationFieldId = normalize(mapping.hyperengage_field);
    if (!sourceField || !destinationFieldId) return;

    const workspaceField = workspaceFields.find((field) => field.id === destinationFieldId);
    const columnName = workspaceField?.column_name || workspaceField?.name || destinationFieldId;
    exportValue[sourceField] = { $var: `$.${columnName}` };
  });

  return exportValue;
};

const stageResult = (
  object: string,
  stage: CommitStage,
  ok: boolean,
  error?: string,
): SaveRunResult => ({ object, stage, ok, error });

export const commitObject = async ({
  object,
  workspaceObjectId,
  providerKey,
  sourceCollection,
  config,
  draftMappings,
  roleMappings,
  relationshipMappings,
  writebackConfig,
  writebackMappings,
  workspaceFields,
  providerFields,
  systemMappingFields,
  inboundSyncId,
  writebackSyncId,
  dependencies,
}: CommitObjectParams): Promise<CommitObjectOutput> => {
  const results: SaveRunResult[] = [];

  let resolvedInboundSyncId = inboundSyncId;
  if (!resolvedInboundSyncId) {
    resolvedInboundSyncId = await dependencies.ensureInboundSync(object, sourceCollection);
    if (!resolvedInboundSyncId) {
      throw new Error(`Could not ensure inbound sync for ${object}`);
    }
  }
  results.push(stageResult(object, "ensure_inbound_sync", true));

  await dependencies.updateSyncConfiguration(resolvedInboundSyncId, {
    source_object: sourceCollection,
    source_object_id: sourceCollection,
    destination_object: toBackendSlug(object),
    destination_object_id: workspaceObjectId,
    matcher_field_name: config?.matcher_field_name || "external_id",
    source_matcher_field: config?.matcher_field_name || "external_id",
    destination_matcher_field: config?.hyperengage_matcher_field || "external_id",
    allow_creates: config?.allow_creates ?? true,
    allow_updates: config?.allow_updates ?? true,
    sync_policy: config?.sync_policy,
    time_window: config?.time_window,
    status: "disabled",
  });
  results.push(stageResult(object, "update_inbound_config", true));

  const inboundDbMappings = buildInboundDbMappings({
    draftMappings,
    matcherFieldName: config?.matcher_field_name,
    matcherHyperengageFieldName: config?.hyperengage_matcher_field || "external_id",
    workspaceFields,
    systemMappingFields,
    roleMappings,
  });
  await persistDbMappings({
    syncId: resolvedInboundSyncId,
    desiredMappings: inboundDbMappings,
    dependencies,
  });
  results.push(stageResult(object, "save_inbound_field_mappings", true));

  const importValue = buildImportValue({
    object,
    providerKey,
    draftMappings,
    matcherFieldName: config?.matcher_field_name,
    matcherHyperengageFieldName: config?.hyperengage_matcher_field || "external_id",
    workspaceFields,
    systemMappingFields,
    roleMappings,
    relationshipMappings,
    providerFields,
  });
  await dependencies.patchIntegrationFieldMapping(object, { importValue });
  results.push(stageResult(object, "save_integration_import_value", true));

  let resolvedWritebackSyncId = writebackSyncId;
  if (writebackConfig.enabled) {
    if (!resolvedWritebackSyncId) {
      resolvedWritebackSyncId = await dependencies.ensureWritebackSync(
        object,
        resolvedInboundSyncId,
        sourceCollection,
      );
      if (!resolvedWritebackSyncId) {
        throw new Error(`Could not ensure writeback sync for ${object}`);
      }
    }
    results.push(stageResult(object, "ensure_writeback_sync", true));

    await dependencies.updateSyncConfiguration(resolvedWritebackSyncId, {
      allow_creates: writebackConfig.allow_creates ?? false,
      allow_updates: writebackConfig.allow_updates ?? true,
      status: "disabled",
    });
    results.push(stageResult(object, "update_writeback_config", true));

    const writebackDbMappings = buildWritebackDbMappings({ writebackMappings, workspaceFields });
    await persistDbMappings({
      syncId: resolvedWritebackSyncId,
      desiredMappings: writebackDbMappings,
      dependencies,
    });
    results.push(stageResult(object, "save_writeback_field_mappings", true));

    const exportValue = buildExportValue({ writebackMappings, workspaceFields });
    await dependencies.patchIntegrationFieldMapping(object, { exportValue });
    results.push(stageResult(object, "save_integration_export_value", true));
  } else if (resolvedWritebackSyncId) {
    await dependencies.updateSyncConfiguration(resolvedWritebackSyncId, { status: "disabled" });
    results.push(stageResult(object, "disable_writeback", true));
  }

  return {
    inboundSyncId: resolvedInboundSyncId,
    writebackSyncId: resolvedWritebackSyncId,
    results,
  };
};
